import api from "./api"; // importar cliente axios configurado con baseURL y Authorization

// Tipo del payload que espera el backend para crear/actualizar evaluación
export type EvaluacionPayload = {
  evaluado_id: number; // id/legajo del evaluado
  evaluador_id?: number; // cédula del evaluador (si se omite, backend usa el usuario de sesión)
  evaluador_cargo?: string; // cargo del evaluador
  fecha_ingreso?: string; // YYYY-MM-DD
  respuestas: {
    responsabilidades: number[]; // arreglo de respuestas de responsabilidades (0..4 en borrador, 1..4 en final)
    habilidades: number[]; // arreglo de respuestas de habilidades (0..4 en borrador, 1..4 en final)
  };
  indicadores: {
    montos: { objetivo: number; cantidad: number }; // datos del indicador de montos
    visitas: { objetivo: number; cantidad: number }; // datos del indicador de visitas
  };
  resumenes?: { // objeto opcional con los 3 resúmenes obligatorios en UI
    fortalezas: string; // texto con las principales fortalezas
    oportunidades: string; // texto con las oportunidades de mejora
    capacitaciones: string; // texto con sugerencias de capacitaciones
  };
  resumenes_seleccion?: { // objeto opcional para selección múltiple (hasta 5 por sección)
    fortalezas_ids: number[]; // ids de fortalezas seleccionadas
    oportunidades_ids: number[]; // ids de oportunidades seleccionadas
    capacitaciones_ids: number[]; // ids de capacitaciones seleccionadas
    otros?: { // textos libres cuando se marca "Otra"
      fortalezas?: string; // texto adicional de fortalezas
      oportunidades?: string; // texto adicional de oportunidades
      capacitaciones?: string; // texto adicional de capacitaciones
    };
  };
  borrador?: boolean; // flag opcional: true = EN_PROCESO (borrador), false/omitido = FINALIZADA
};

// Respuesta mínima de creación/actualización
export type EvaluacionCreada = {
  id: number; // id generado o actualizado
  totales: any; // totales por sección (tipado laxo para no bloquear)
  final: any; // resumen final
};

export type EstadoEvaluacionReporte = "FINALIZADA" | "EN_PROCESO" | "SIN_EVALUACION";

export type ReportePersona = {
  legajo?: number;
  cedula?: number;
  nombre: string;
  estado: EstadoEvaluacionReporte;
  puntos_servicios?: string[];
};

export type ReporteJerarquiaItem = {
  fiscal: { cedula: number; nombre: string };
  supervisores: Array<{ supervisor: ReportePersona; operarios: ReportePersona[] }>;
  operarios: ReportePersona[];
  resumen: { finalizadas: number; pendientes: number; sin_evaluacion: number; total: number };
};

export type FiscalReporte = { cedula: number; nombre: string };

export type PuntoServicioReporte = { id: number; nombre: string };

const EvaluacionAPI = {
  async crear(payload: EvaluacionPayload) {
    // invocar endpoint POST del backend para crear evaluación (borrador o final)
    const { data } = await api.post<EvaluacionCreada>(
      "/evaluaciones/desempeno",
      payload
    );
    return data; // devolver estructura con id y totales
  },

  async actualizar(id: number, payload: EvaluacionPayload) {
    // invocar endpoint PUT del backend para actualizar evaluación existente
    const { data } = await api.put<EvaluacionCreada>(
      `/evaluaciones/desempeno/${id}`,
      payload
    );
    return data; // devolver estructura con id y totales recalculados
  },

  async obtener(id: number) {
    // obtener detalle completo de una evaluación por id
    const { data } = await api.get(`/evaluaciones/desempeno/${id}`);
    return data; // devolver JSON con cabecera, respuestas e indicadores
  },

  async listar(params?: { evaluado_id?: number; evaluador_id?: number; desde?: string; hasta?: string; estado?: "EN_PROCESO" | "FINALIZADA" }) {
    // armar query string con filtros opcionales (evaluado, evaluador, fechas y estado)
    const q = new URLSearchParams(); // construir URLSearchParams para mapear filtros a query string
    if (params?.evaluado_id) q.set('evaluado_id', String(params.evaluado_id)); // filtrar por evaluado_id si viene
    if (params?.evaluador_id) q.set('evaluador_id', String(params.evaluador_id)); // filtrar por evaluador_id si viene
    if (params?.desde) q.set('desde', params.desde); // filtrar por fecha desde
    if (params?.hasta) q.set('hasta', params.hasta); // filtrar por fecha hasta
    if (params?.estado) q.set('estado', params.estado); // filtrar por estado (EN_PROCESO / FINALIZADA) si viene
    const path = q.toString() ? `/evaluaciones/desempeno/listar?${q.toString()}` : `/evaluaciones/desempeno/listar`; // decidir URL con o sin query
    const { data } = await api.get(path); // ejecutar request GET
    // devolver lista tipada de evaluaciones (la API por ahora no expone el campo estado en esta respuesta)
    return data as { datos: Array<{ id: number; evaluado_id: number; evaluador_id: number; evaluador_cargo: string; fecha_evaluacion: string; porcentaje_final: number }> };
  },

  async listarFiscalesReporte(q: string) {
    const qs = new URLSearchParams();
    if (q) qs.set('q', String(q));
    const suffix = qs.toString();
    const path = suffix ? `/evaluaciones/desempeno/fiscales?${suffix}` : `/evaluaciones/desempeno/fiscales`;
    const { data } = await api.get(path, { timeout: 30000 });
    return (data?.datos || []) as FiscalReporte[];
  },

  async listarPuntosServicioReporte(fiscalCedula: number, q?: string) {
    const qs = new URLSearchParams();
    qs.set('fiscal_cedula', String(fiscalCedula));
    if (q) qs.set('q', String(q));
    const { data } = await api.get(`/evaluaciones/desempeno/puntos_servicio?${qs.toString()}`, { timeout: 30000 });
    return (data?.datos || []) as PuntoServicioReporte[];
  },

  async reporteJerarquia(dias: number = 90, fiscalCedula?: number, psId?: number) {
    const q = new URLSearchParams();
    q.set('dias', String(dias));
    if (typeof fiscalCedula === 'number') q.set('fiscal_cedula', String(fiscalCedula));
    if (typeof psId === 'number') q.set('ps_id', String(psId));
    const { data } = await api.get(
      `/evaluaciones/desempeno/reporte_jerarquia?${q.toString()}`,
      { timeout: 120000 }
    );
    return data as { desde: string; hasta: string; dias: number; datos: ReporteJerarquiaItem[] };
  },

  async obtenerCapacitacionesAnuales(
    empleadoId: number, // código de empleado (CodEmpleado)
    anio?: number, // año consultado (opcional)
    detalle?: boolean, // si se solicitan filas de detalle
    opts?: { timeoutMs?: number } // opciones extra para control de timeout
  ) {
    // construir query string con parámetros opcionales
    const q = new URLSearchParams(); // crear instancia de URLSearchParams
    q.set('empleado_id', String(empleadoId)); // setear id del empleado requerido por la API
    if (typeof anio === 'number') q.set('anio', String(anio)); // setear año si se provee explícitamente
    if (typeof detalle === 'boolean') q.set('detalle', detalle ? 'true' : 'false'); // setear flag de detalle si viene
    // invocar endpoint GET del backend
    const { data } = await api.get(
      `/evaluaciones/supervisor/capacitaciones?${q.toString()}`, // URL con query construida
      {
        timeout: opts?.timeoutMs ?? 600000, // elevar el timeout a 60s (configurable)
      } // llamada HTTP con configuración específica
    ); // llamada HTTP con configuración específica
    return data as { datos: { anio: number; cod_empleado: number; cantidad: number; detalle?: Array<{ fecha: string; capacitador: string; capacitacion: string; modalidad: string; origen: string; horas: number }> } }; // tipado de la respuesta
  },

  async obtenerSancionesAnuales(
    empleadoId: number, // id/legajo del empleado (funcod)
    anio?: number, // año consultado (opcional; por defecto año actual)
    detalle?: boolean, // si se requieren filas detalladas
    opts?: { timeoutMs?: number } // opciones de timeout
  ) {
    const q = new URLSearchParams(); // construir query string
    q.set('empleado_id', String(empleadoId)); // setear id del empleado
    if (typeof anio === 'number') q.set('anio', String(anio)); // setear año si se provee
    if (typeof detalle === 'boolean') q.set('detalle', detalle ? 'true' : 'false'); // setear flag detalle si llega
    const { data } = await api.get(
      `/evaluaciones/operario/sanciones?${q.toString()}`, // endpoint de sanciones anuales
      { timeout: opts?.timeoutMs ?? 30000 } // configurar timeout (default 30s)
    ); // ejecutar request
    return data as { datos: { anio: number; cod_empleado: number; cantidad: number; detalle?: Array<{ funcod: number; fecha_inicio: string; fecha_fin: string; auscod: number; austip: number; auscom: string }> } }; // tipado de respuesta
  },
};

export default EvaluacionAPI;


